<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Performance por Hora',
  },
});

const maxConversations = computed(() => {
  return Math.max(...props.data.map(d => d.conversations || 0), 1);
});

function getBarHeight(value) {
  return (value / maxConversations.value) * 100;
}

function getResponseTimeColor(minutes) {
  if (minutes <= 5) return 'text-n-teal-11';
  if (minutes <= 15) return 'text-n-blue-11';
  if (minutes <= 30) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-clock text-lg text-n-amber-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="data.length === 0"
      class="flex items-center justify-center h-48 text-n-slate-11"
    >
      <span class="text-sm">Sem dados disponiveis</span>
    </div>

    <!-- Chart -->
    <div v-else class="flex flex-col gap-2">
      <div class="flex items-end gap-0.5 h-32">
        <div
          v-for="hour in data"
          :key="hour.hour"
          class="flex-1 flex flex-col items-center justify-end group relative"
        >
          <div
            class="w-full rounded-t transition-all duration-300"
            :class="hour.is_peak ? 'bg-n-amber-9' : 'bg-n-blue-9'"
            :style="{
              height: `${getBarHeight(hour.conversations)}%`,
              minHeight: hour.conversations > 0 ? '2px' : '0',
            }"
          />
          <!-- Tooltip -->
          <div class="absolute bottom-full mb-1 hidden group-hover:block z-10">
            <div
              class="bg-n-solid-1 border border-n-weak rounded px-2 py-1 text-xs shadow-lg whitespace-nowrap"
            >
              <div class="font-medium text-n-slate-12">{{ hour.label }}</div>
              <div class="text-n-slate-11">
                {{ hour.conversations }} conversas
              </div>
              <div :class="getResponseTimeColor(hour.avg_response_minutes)">
                {{ hour.avg_response_minutes }} min resp.
              </div>
              <div v-if="hour.is_peak" class="text-n-amber-11">
                Horario de pico
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- X-axis labels -->
      <div class="flex gap-0.5">
        <div v-for="hour in data" :key="hour.hour" class="flex-1 text-center">
          <span v-if="hour.hour % 4 === 0" class="text-xs text-n-slate-10">
            {{ hour.label }}
          </span>
        </div>
      </div>

      <!-- Legend -->
      <div
        class="flex items-center justify-center gap-4 pt-2 border-t border-n-weak text-xs"
      >
        <div class="flex items-center gap-1">
          <span class="w-3 h-3 rounded bg-n-blue-9" />
          <span class="text-n-slate-11">Normal</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-3 h-3 rounded bg-n-amber-9" />
          <span class="text-n-slate-11">Pico</span>
        </div>
      </div>
    </div>
  </div>
</template>
